import {
  togglePostModalEventDispatch,
  togglePostSidebarFullScreenEventDispatch
} from 'src/utils/customEvents'
import { MODAL_ACTIONS } from 'src/utils/customEvents/types'
import clsx from 'clsx'
import { UploadBox } from 'src/components/InputMarkdown/components'
import { PostType } from 'src/hook/usePosts'
import { MediaCard } from 'src/components/mediaCard'
import { Grid } from 'src/components/grid'
import { faClose } from '@fortawesome/pro-light-svg-icons/faClose'
import { resetScroll } from 'src/utils/functions'
import { usePostModalUpload, useCreatePublication } from '../../hooks'
import { PostModalHeader } from '../postModalHeader'
import { PostModalContent } from '../postModalContent'
import { PostModalFooter } from '../postModalFooter'
import { PostModalActions } from '../postModalActions'
import { getDefaultPublication } from '../..'
import { RemoveMediaButton } from '../removeMediaButton'
import { PostModalPreviewVideo } from '../postModalPreviewVideo'
import { PostSidebarFullProps } from './postSidebarFull.types'
import {
  CustomSidebar,
  ContainerSidebar,
  ContainerSidebarContent,
  ContainerSidebarFooter,
  CustomSidebarHeader
} from './postSidebarFull.styles'

const PostSidebarFull = ({
  isOpen,
  title,
  description,
  postType,
  media,
  currentMedia,
  isOpenUpload,
  fileUploaded,
  uploading,
  isFileUploaded,
  descriptionOnlyText,
  linkUrl,
  setLinkUrl,
  onClose,
  setFileUploaded,
  setTitle,
  setDescription,
  setDescriptionOnlyText,
  setPostType,
  setMedia,
  setCurrentMedia,
  setUploading,
  setIsOpenUpload,
  onRemoveMedia,
  handleResetForm
}: PostSidebarFullProps) => {
  const { isLoadingCreatePublication, onCreatePublication } = useCreatePublication()

  const { onDropZoneSuccess } = usePostModalUpload({
    setPostType,
    setMedia,
    setCurrentMedia,
    setIsOpenUpload,
    setFileUploaded
  })

  if (!isOpen) return null

  const handleRemoveFullScreen = () => {
    togglePostModalEventDispatch({ action: MODAL_ACTIONS.OPEN })
    togglePostSidebarFullScreenEventDispatch({ action: MODAL_ACTIONS.CLOSE })
  }

  const handleSubmit = async () => {
    const publication = {
      ...getDefaultPublication(),
      ...media,
      title,
      description,
      postType
    }

    try {
      await onCreatePublication({
        publication,
        descriptionOnlyText,
        handleResetForm,
        isFullScreen: true
      })

      resetScroll()
    } catch {
      throw new Error('Error when publishing on full screen modal')
    }
  }

  const postModalActionsClassNames = clsx(
    '_d-flex _justify-content-start _align-items-center _w-full _py-5 _px-7',
    {
      '_border-bottom _border-top _border-gray-200': !isOpenUpload,
      '_border-0': isOpenUpload
    }
  )

  const hasFileUploaded = fileUploaded.name && fileUploaded.size

  const removeMedia = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    setIsOpenUpload?.(false)
  }

  return (
    <CustomSidebar isOpen={isOpen} onClose={onClose}>
      <ContainerSidebar className="_vw-full _vh-full _overflow-y-auto _overflow-x-hidden">
        <CustomSidebarHeader className="_d-flex _justify-content-start _vw-full _border-bottom _border-gray-200">
          <PostModalHeader
            className="_w-full"
            handleModalFullScreen={handleRemoveFullScreen}
            handleOnClose={onClose}
            isFullScreen
          />
        </CustomSidebarHeader>

        <div className="_d-flex _flex-column _justify-content-start align-items-start _vw-full">
          <ContainerSidebarContent className="_w-full _v-full _position-relative _overflow-x-hidden _pt-6">
            <Grid.Row>
              <Grid.Col
                xs={{ offset: 1, size: 10 }}
                md={{ offset: 2, size: 8 }}
                lg={{ offset: 2, size: 8 }}
                id="post-content-container"
              >
                <PostModalContent
                  isFullScreen
                  title={title}
                  linkUrl={linkUrl}
                  postType={postType}
                  setPostType={setPostType}
                  setTitle={setTitle}
                  setLinkUrl={setLinkUrl}
                  description={description}
                  isOpenUpload={isOpenUpload}
                  isFileUploaded={isFileUploaded}
                  setDescription={setDescription}
                  setDescriptionOnlyText={setDescriptionOnlyText}
                />
              </Grid.Col>
            </Grid.Row>
          </ContainerSidebarContent>
        </div>

        <ContainerSidebarFooter className="_d-flex _flex-column _justify-content-center align-items-start">
          {isOpenUpload && currentMedia && hasFileUploaded && (
            <div className="_border-top _border-gray-200 _w-full _pt-5 _d-flex _justify-content-start _align-items-center _px-8">
              {PostType.VIDEO === postType && (
                <PostModalPreviewVideo linkVideo={currentMedia} onRemoveMedia={onRemoveMedia} />
              )}

              {PostType.IMAGE === postType && (
                <MediaCard
                  className="_max-w-full"
                  name={fileUploaded.name}
                  size={fileUploaded.size}
                  extension={fileUploaded.extension}
                  thumbnailUrl={currentMedia}
                  onDelete={onRemoveMedia}
                />
              )}
            </div>
          )}

          {isOpenUpload && !currentMedia && !hasFileUploaded && (
            <div className="_border-top _border-gray-200 _w-full _pt-6">
              <UploadBox
                classNames="_mb-2 _mx-7"
                mediaType={PostType.MEDIA}
                onLoading={setUploading}
                onSuccess={onDropZoneSuccess}
              >
                <RemoveMediaButton onRemove={removeMedia} icon={faClose} />
              </UploadBox>
            </div>
          )}

          <PostModalActions
            className={postModalActionsClassNames}
            setPostType={setPostType}
            setMedia={setMedia}
            setCurrentMedia={setCurrentMedia}
            setIsOpenUpload={setIsOpenUpload}
            setFileUploaded={setFileUploaded}
            postType={postType}
            isFileUploaded={isFileUploaded}
            isOpenUpload={isOpenUpload}
            setUploading={setUploading}
          />

          <PostModalFooter
            className="_d-flex _justify-content-end align-items-center _bg-gray-100 _py-4 _px-4"
            title={title}
            onSubmit={handleSubmit}
            isLoadingSubmit={isLoadingCreatePublication || uploading}
            isFullScreen
          />
        </ContainerSidebarFooter>
      </ContainerSidebar>
    </CustomSidebar>
  )
}

export default PostSidebarFull
